import { Component, inject, OnInit, viewChild } from '@angular/core';
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from "@angular/material/icon";
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import {
  DataBindingDirective,
  KENDO_GRID,
} from "@progress/kendo-angular-grid";
import { KENDO_GRID_PAGE_SIZES } from "../../../../../constants/api.constant";
import { SpinnerService } from "../../../../../shared/spinner/spinner.service";
import { AlertService } from "../../../../../shared/alert/alert.service";
import { ApiService } from "../../../../../services/api.service";
import { InfoButtonTooltipComponent } from "../../../../../shared/info-button-tooltip/info-button-tooltip.component";
import {
  KENDO_PAGER,
} from "@progress/kendo-angular-pager";
import { InputsModule } from "@progress/kendo-angular-inputs";
import {
  KENDO_LISTVIEW,
} from "@progress/kendo-angular-listview";
import { AgentService } from "../../../../agent/services/agent.service";
import { SensitivityLabelService } from "../../../../../services/sensitivity-label.service";
import { CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { WorkflowBuilderService } from "../services/workflow-builder.service";
import { IInstructions } from "../../../interfaces/i-instructions";
import { IWorkflowDetails } from "../../../interfaces/i-workflow";
import { AlertEnum } from "../../../../../shared/alert/alert.enum";
import { InstructionType } from "../../../../../enums/automation.enum";
import { ISensitivityLabel } from "../../../../../interfaces/i-sensitivity-label";
import { mapLegacySensitivityLabel } from "../../../../../utilities/sensitivity-label.utils";
import { ProfileService } from '../../../../../services/profile.service';
import { MatBottomSheet, MatBottomSheetConfig } from '@angular/material/bottom-sheet';
import { PromptLibraryListItemSummaryComponent } from '../../../../ai-chat/prompt-library/prompt-library-list/prompt-library-list-item-summary/prompt-library-list-item-summary.component';
import { AutomationUtilitiesService } from '../../../../ai-chat/services/automation-utilities.service';
import { SortDescriptor } from '@progress/kendo-data-query';
import { SVGIcon, chevronDownIcon, chevronUpIcon, filterIcon } from '@progress/kendo-svg-icons';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';

/**
 * WorkflowInstructionsComponent manages the instructions step of the workflow builder.
 * It handles instruction selection, filtering, ordering, and validation, and synchronizes
 * selected instructions with the workflow service.
 */
@Component({
  selector: 'workflow-instructions',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
    KENDO_GRID,
    KENDO_PAGER,
    KENDO_LISTVIEW,
    KENDO_BUTTONS,
    InputsModule,
    InfoButtonTooltipComponent,
  ],
  templateUrl: './workflow-instructions.component.html',
  styleUrl: './workflow-instructions.component.scss'
})
export class WorkflowInstructionsComponent implements OnInit {
  // Kendo UI Grid configuration
  dataBinding = viewChild<DataBindingDirective>(DataBindingDirective);
  gridData: IInstructions[] = [];
  gridView: IInstructions[] = [];
  pageSize = 10;
  pageSizes = KENDO_GRID_PAGE_SIZES;
  total = 0;
  expandedDetailKeys: string[] = [];
  typeColumnWidth = 140;  // Width of the date columns.
  dateColumnWidth = 135;  // Width of the date columns.
  dateModifiedColumnWidth = 160;  // Width of the date columns.
  // END Kendo UI Grid configuration
  instructionForm!: FormGroup;
  showActionColumn: boolean = false;

  // Table sorting
  sort: SortDescriptor[] = [];
  sortIcon: SVGIcon = filterIcon;
  readonly sortAscSvg: SVGIcon = chevronUpIcon;
  readonly sortDescSvg: SVGIcon = chevronDownIcon;

  agentService = inject(AgentService);
  #sensitivityLabelService = inject(SensitivityLabelService);
  /** Whether the sensitivity-label feature is enabled; label rows are hidden when disabled. */
  readonly slEnabled = this.#sensitivityLabelService.enabled;
  // Use inject() for LiveAnnouncer per project standards
  #liveAnnouncer = inject(LiveAnnouncer);
  #spinnerService = inject(SpinnerService);
  #alertService = inject(AlertService);
  #apiService = inject(ApiService);
  #workflowBuilderService = inject(WorkflowBuilderService);
  #profileService = inject(ProfileService);
  #automationUtilitiesService = inject(AutomationUtilitiesService);

  selectedKeys: string[] = [];
  selectedInstructions: IInstructions[] = [];

  constructor() {
    this.showActionColumn = this.#profileService.userProfile()?.config?.canManageWorkflows || false;
    if (this.#workflowBuilderService.workflow()) {
      // Only fetch available instructions; do not set gridData from workflow signal
      this.#fetchInstructions();
    }
    this.selectedInstructions = this.#workflowBuilderService.instructions() || [];
    // Initialize selectedKeys based on selectedInstructions
    this.selectedKeys = this.selectedInstructions.map(i => i.id) || [];
  }

  ngOnInit() {
  }

  /**
   * Add selectedInstructions with selectedKeys from the grid.
   * Items are added to the end of the selectedInstructions array in the order they are selected.
   */
  onSelectionChange(keys: string[]): void {
    this.selectedInstructions = keys.map(key => this.gridData.find(i => i.id === key)).filter(Boolean) as IInstructions[];
    this.selectedKeys = keys;
    this.instructionForm.get('instructions')?.setValue(this.selectedInstructions);
    this.instructionForm.get('instructions')?.markAsTouched();
  }

  /**
   * Handles selection and deselection of an item from the Kendo UI grid.
   * Adds the selected item to the end of selectedInstructions and updates selectedKeys.
   * Removes the deselected item from selectedInstructions and updates workflow and instructions signals.
   *
   * @param item The selection event from the grid.
   */
  onSelectItem(item: unknown): void {
    if (
      typeof item === 'object' &&
      item !== null &&
      Array.isArray((item as any).selectedRows)
    ) {
      // Handle selection
      if ((item as any).selectedRows.length > 0) {
        const dataItem = (item as any).selectedRows[0].dataItem as IInstructions;
        if (!this.selectedInstructions.some(i => i.id === dataItem.id)) {
          this.selectedInstructions.push(dataItem);
          this.selectedKeys.push(dataItem.id);
        }
      }
      // Handle deselection
      if (Array.isArray((item as any).deselectedRows) && (item as any).deselectedRows.length > 0) {
        const deselectedId = (item as any).deselectedRows[0].dataItem.id;
        this.selectedInstructions = this.selectedInstructions.filter(i => i.id !== deselectedId);
        this.selectedKeys = this.selectedKeys.filter(id => id !== deselectedId);
      }
      // Update signals and workflow
      this.#workflowBuilderService.instructions.set(this.selectedInstructions);
      this.updateWorkflow();
      this.instructionForm.get('instructions')?.setValue(this.selectedInstructions);
      this.instructionForm.get('instructions')?.markAsTouched();
    }
  }

  /**
   * Filter the grid data based on the input value, but do not filter nested arrays.
   * If a match is found in any main or nested field, show the full details for that instruction.
   */
  onFilter(value: string): void {
    const search = value?.toString().toLowerCase() ?? '';
    if (!search) {
      this.gridView = this.gridData.map(item => ({ ...item }));
      this.total = this.gridView.length;
      const dataBindingInstance = this.dataBinding();
      if (dataBindingInstance) {
        dataBindingInstance.skip = 0;
      }
      return;
    }

    this.gridView = this.gridData.filter(item => {
      // Check main fields
      const mainMatch = [
        item.details.name,
        item.details.description,
        item.details.prompt,
        item.details.type,
        item.details.keywords ? item.details.keywords.join(', ') : '',
        item.details.fileUploadLabel,
        item.details.collectInformationLabel,
        item.createdDate ? new Date(item.createdDate).toLocaleDateString() : '',
        item.modifiedDate ? new Date(item.modifiedDate).toLocaleDateString() : ''
      ].some(field => field?.toString().toLowerCase().includes(search));

      // Check nested arrays for any match
      const ownersMatch = (item.details.owners || []).some(o =>
        o.name?.toLowerCase().includes(search) || o.userId?.toLowerCase().includes(search)
      );
      const usersMatch = (item.details.users || []).some(u =>
        u.name?.toLowerCase().includes(search) || u.userId?.toLowerCase().includes(search)
      );
      const groupsMatch = (item.details.groups || []).some(g =>
        g.displayName?.toLowerCase().includes(search) || g.id?.toLowerCase().includes(search)
      );
      const sensitivityLabelsMatch = (item.details.sensitivityLabels || []).some(l =>
        this.agentService.getSensitivityLabelString(l).toLowerCase().includes(search)
      );

      return mainMatch || ownersMatch || usersMatch || groupsMatch || sensitivityLabelsMatch;
    });

    const dataBindingInstance = this.dataBinding();
    if (dataBindingInstance) {
      dataBindingInstance.skip = 0;
    }
    this.total = this.gridView.length;
  }

  /**
   * Expands the details row for the given instruction in the grid.
   * @param dataItem The instruction item to expand.
   * @returns The unique identifier for the instruction.
   */
  expandDetailsBy = (dataItem: IInstructions): string => {
    return dataItem.id;
  };

  /**
   * Moves an instruction up in the selectedInstructions list.
   * @param index Index of the instruction to move up
   */
  moveInstructionUp(index: number): void {
    if (index <= 0) return;
    moveItemInArray(this.selectedInstructions, index, index - 1);
    // Update the instructions signal with the new order
    this.#workflowBuilderService.instructions.set(this.selectedInstructions);

    this.#announceReorder(index - 1);
  }

  /**
   * Moves an instruction down in the selectedInstructions list.
   * @param index Index of the instruction to move down
   */
  moveInstructionDown(index: number): void {
    if (index >= this.selectedInstructions.length - 1) return;
    moveItemInArray(this.selectedInstructions, index, index + 1);
    // Update the instructions signal with the new order
    this.#workflowBuilderService.instructions.set(this.selectedInstructions);

    this.#announceReorder(index + 1);
  }

  /**
   * Handles drag-and-drop reordering of selected instructions.
   */
  onDragAndDropInstruction(event: CdkDragDrop<IInstructions[]>): void {
    moveItemInArray(this.selectedInstructions, event.previousIndex, event.currentIndex);
    // Update the instructions signal with the new order
    this.#workflowBuilderService.instructions.set(this.selectedInstructions);
  }

  /**
   * Validates the selected instructions.
   *
   * Checks if there is at least one instruction selected and enabled.
   * This method is called from the parent component.
   *
   * @returns `true` if there is at least one enabled selected instruction, otherwise `false`.
   */
  isValid(): boolean {
    return this.instructionForm.valid;
  }

  /**
   * Initializes the instruction form with the currently selected instructions.
   * Sets up form controls and validation for the instructions step.
   */
  setForm() {
    this.instructionForm = new FormGroup({
      instructions: new FormControl(this.selectedInstructions.length > 0 ? this.selectedInstructions : null, [ Validators.required ])
    });
  }

  /**
   * Updates the workflow signal by setting the `instructions` property
   * inside the `details` object of the workflow.
   *
   * This ensures that the selected instructions are correctly nested
   * under `details.instructions` in accordance with the `IWorkflow` interface.
   *
   * Usage:
   * Call this method after modifying the selected instructions to persist
   * the changes to the workflow state.
   */
  updateWorkflow() {
    this.#workflowBuilderService.workflow.update(prev => ({
      ...prev,
      details: {
        ...prev.details,
        instructions: this.#workflowBuilderService.instructions().map(i => i.id)
      } as IWorkflowDetails
    }));
  }

  /**
   * Marks all controls in the workflow form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the WorkflowBuilderComponent.
   */
  markFormAsTouched(): void {
    this.instructionForm.markAllAsTouched();
  }

  /**
   * Removes an instruction from the selectedInstructions and selectedKeys arrays.
   * Updates the workflow and form accordingly.
   *
   * @param instructionId The ID of the instruction to remove.
   */
  removeInstruction(instructionId: string): void {
    this.selectedInstructions = this.selectedInstructions.filter(i => i.id !== instructionId);
    this.selectedKeys = this.selectedKeys.filter(id => id !== instructionId);
    this.#workflowBuilderService.instructions.set(this.selectedInstructions);
    this.updateWorkflow();
    this.instructionForm.get('instructions')?.setValue(this.selectedInstructions);
  }

  /**
   * Fetches available instructions from the API and updates the grid data.
   * Also synchronizes selected instructions with the workflow service and initializes the form.
   * Handles errors and loading state.
   *
   * @private
   */
  #fetchInstructions(): void {
    this.#spinnerService.show();

    this.#apiService.getInstructions().subscribe({
      next: instructions => {
        this.gridData = this.#filterInstructionsBySensitivityLabels(instructions);
        this.gridView = this.gridData;
        this.total = this.gridView.length;
        // Set selectedInstructions here
        // Use details.instructions if available, fallback to workflow.instructions for backward compatibility
        let workflowInstructionIds: string[] = [];
        const workflow = this.#workflowBuilderService.workflow();
        if (workflow.details && workflow.details.instructions) {
          workflowInstructionIds = workflow.details.instructions as string[];
        } else if ((workflow as any).instructions) {
          workflowInstructionIds = ((workflow as any).instructions || []).map((i: any) => i && i.id ? i.id : i) || [];
        }
        let selectedInstructions: IInstructions[] = [];
        if (workflowInstructionIds.length > 0 && Array.isArray(instructions)) {
          // Preserve the order from workflowInstructionIds
          selectedInstructions = workflowInstructionIds
            .map(id => instructions.find((instr: any) => instr.id === id))
            .filter(Boolean) as IInstructions[];
        }
        this.#workflowBuilderService.instructions.set(selectedInstructions);
        this.selectedInstructions = selectedInstructions;
        this.selectedKeys = this.selectedInstructions.map(i => i.id);
        this.setForm();
        
        // Sort the checkbox column initially to show selected items at the top
        this.sortCheckboxColumn();

        this.#spinnerService.hide();
      },
      error: (error: any) => {
        this.#spinnerService.hide();
        this.#alertService.showAlert({
          message: `Failed to get instructions.`,
          type: AlertEnum.error,
        });
      }
    });
  }

  /**
   * Announces the new position of the instruction for screen readers.
   * @param index The new index of the moved instruction
   */
  #announceReorder(index: number): void {
    const instruction = this.selectedInstructions[index];
    const name = instruction?.details.name || 'Instruction';
    this.#liveAnnouncer.announce(`${name} moved to position ${index + 1}`, 'polite');
  }

  /**
   * Filters instructions based on sensitivity labels only for type "Agent".
   * Other instruction types are returned without filtering.
   * Agent instructions must have at least one sensitivity label that matches the workflow's labels.
   *
   * @param instructions Array of IInstructions to filter.
   * @returns Filtered array of IInstructions.
   */
  #filterInstructionsBySensitivityLabels(instructions: IInstructions[]): IInstructions[] {
    const workflowLabels = this.#workflowBuilderService.workflow().details?.sensitivityLabels ?? [];
    if (workflowLabels.length === 0) {
      return instructions;
    }
    // Resolve each stored thin ref to its canonical code so comparison works across mixed
    // shapes ({ code } vs legacy { id, guid, label }). The legacy `id` is a placeholder
    // (often 0 or undefined) on backfilled data and must not be used as the match key.
    const workflowCodes = new Set(
      workflowLabels.map(l => this.#canonicalLabelCode(l)).filter((c): c is string => !!c)
    );
    if (workflowCodes.size === 0) {
      return instructions;
    }
    return instructions.filter(instr => {
      if (instr.details.type !== InstructionType.Agent) {
        return true;
      }
      const instrLabels = instr.details.sensitivityLabels ?? [];
      if (instrLabels.length === 0) return false;
      // Check if there's any overlap between instruction and workflow labels
      return instrLabels.some(label => {
        const code = this.#canonicalLabelCode(label);
        return !!code && workflowCodes.has(code);
      });
    });
  }

  /**
   * Resolves a stored sensitivity-label thin reference to its canonical code, using the
   * registered label list. Falls back to the raw `code` field when the label list is not
   * yet loaded so filtering still works across `{ code }` and legacy shapes.
   *
   * @param label The stored sensitivity-label thin reference.
   * @returns The canonical code, or `undefined` when it cannot be resolved.
   */
  #canonicalLabelCode(label: ISensitivityLabel): string | undefined {
    return mapLegacySensitivityLabel(label)?.id ?? label.code;
  }

  /**
   * Checks if the current user can edit the Instruction.
   * @param instruction - The instruction item to check.
   * @returns True if the user can edit the Instruction, else false.
   */
  canUserEditInstruction(instruction: IInstructions): boolean {
    const currentUserEmail = this.#profileService.userProfile()?.profile.email;
    const isOwner = instruction.details.owners.some(owner => owner.userId === currentUserEmail);
    const canManageWorkflows = this.#profileService.userProfile()?.config?.canManageWorkflows || false;
    const isAdmin = this.#profileService.userProfile()?.config?.isAdmin || false;
    return isOwner || canManageWorkflows || isAdmin;
  }


  onSummary(instruction: IInstructions, event?: MouseEvent): void {
        // Stop event propagation to prevent row selection
        if (event) {
            event.stopPropagation();
        }
        this.#automationUtilitiesService.handleInstructionPrompt(instruction);
  }
  
  // Commenting out this code for now since we will need it later down the line
  // /**
  //  * Handles the edit action for a instruction item.
  //  * @param instruction - The instruction item to edit.
  //  */
  // onEdit(instruction: IInstructions): void {
  //   this.#automationUtilityService.editItem(instruction.id!, AutomationType.INSTRUCTION, AutomationAction.EDIT);
  // }

  // /**
  //  * Handles the clone action for a instruction item.
  //  * @param instruction - The instruction item to clone.
  //  */
  // onClone(instruction: IInstructions): void {
  //   this.#automationUtilityService.cloneItem(instruction.id!, AutomationType.INSTRUCTION, AutomationAction.CLONE);
  // }


    sortCheckboxColumn(): void {
    // Toggle direction: If no sort exists or current sort is 'desc', then sort 'asc', otherwise sort 'desc'
    const newSortDir = this.sort.length === 0 || this.sort[0].dir === 'desc' ? 'asc' : 'desc';
    
    // Update sort state first
    this.sort = [{ field: 'checkboxField', dir: newSortDir }];
    this.sortIcon = newSortDir === 'asc' ? this.sortAscSvg : this.sortDescSvg;
    
    // Sort based on the new direction
    this.gridView = [...this.gridView].sort((a, b) => {
      const aSelected = this.selectedKeys.includes(a.id);
      const bSelected = this.selectedKeys.includes(b.id);

      if (aSelected && !bSelected) {
        // For ascending: selected items at top (-1), for descending: selected items at bottom (1)
        return newSortDir === 'asc' ? -1 : 1;
      } else if (!aSelected && bSelected) {
        return newSortDir === 'asc' ? 1 : -1;
      } else {
        return 0; // Keep the same order for items with the same selection status
      }
    });
  }
}
